import styled, {css} from 'styled-components';
import {CommonInteractionStyling} from '../common';
import {BREAKPOINTS, COLORS, ComponentTextStyle, focusStyles} from '../styles';
import {
  ComponentMStyling,
  ComponentLStyling,
  ComponentSStyling,
  ComponentXSStyling,
  ComponentXXSStyling
} from '../styles/typography';
import {Size} from '../types';
import {Z_INDEXES} from '../styles/z-indexes';
import { TooltipTrigger, Tooltip } from '../Tooltips/TooltipStyles';

export const DropdownContainer = styled.div<{ isButton: boolean; readOnly?: boolean; disabled?: boolean; margin?: string; minWidth?: string }>`
  display: inline-block;

  ${(props) => !props.isButton && (props?.minWidth ? `width: 100%; min-width: ${props.minWidth};` : 'width: 100%; min-width: 344px;')}
  ${(props) => (props.readOnly || props.disabled ? 'cursor: not-allowed;' : '')}
  ${(props) => (props.margin ? `margin: ${props.margin};` : 'margin-bottom: 4px;')}
  .show {
    display: block;
  }

  .value {
    flex-grow: 1;
  }

  &:focus-within {
    z-index: ${Z_INDEXES.focus};
  }

  input:hover + div {
    color: ${COLORS.primary_600};
  }

  input:active + div {
    color: ${COLORS.primary_800};
  }


`;

const lockedState = css`
  background-color: ${COLORS.neutral_100};
  box-shadow: inset 0px 0px 0px 1px ${COLORS.neutral_300};
  color: ${COLORS.neutral_600};
  pointer-events: none;
  cursor: not-allowed;

  input {
    color: ${COLORS.neutral_600} !important;
  }

  .dropdown-arrow {
    color: ${COLORS.neutral_300} !important;
  }
`;

const disabledState = css`
  background-color: ${COLORS.white};
  box-shadow: inset 0px 0px 0px 1px ${COLORS.neutral_100};
  color: ${COLORS.neutral_300};
  pointer-events: none;
  cursor: not-allowed;

  .dropdown-arrow {
    color: ${COLORS.neutral_300} !important;
  }

  input {
    color: ${COLORS.neutral_300} !important;

    ::placeholder {
      color: ${COLORS.neutral_300} !important;
    }
  }
`;

const activeValidationMessage = css`
  box-shadow: inset 0px 0px 0px 2px ${COLORS.critical_400};
`;

const placeholderStyling = css`
  ${ComponentMStyling(ComponentTextStyle.Italic, COLORS.neutral_600)}
`;

export const DropdownInputField = styled.input<{ellipsis?: boolean}>`
  border: none;
  outline: none;
  background-color: inherit;
  cursor: pointer;
  color: ${COLORS.black} !important;
  padding: 0;
  flex-grow: 1;
  display: flex;
  justify-content: center;
  overflow: hidden;
  ${props => props.ellipsis ? 'text-overflow: ellipsis;' : ''}
  
  ::placeholder {
    color: ${COLORS.neutral_600};
    font-style: italic;
  }

  ::-webkit-search-decoration,
  ::-webkit-search-cancel-button,
  ::-webkit-search-results-button,
  ::-webkit-search-results-decoration {
    -webkit-appearance: none;
  }
`;

export const DropdownInputFieldStyles = styled.div<{ disabled: boolean; readOnly: boolean; showValidationMessage?: boolean; isPlaceholder?: boolean; fontSize?: string; minWidth?: string }>`
  ${ComponentSStyling(ComponentTextStyle.Regular, 'inherit')}

  padding: 12px 16px;
  height: 48px;
  min-width: ${(props) => (props.minWidth ? `${props.minWidth}` : '344px')};
  width: 100%;

  background: ${COLORS.white};
  box-shadow: inset 0px 0px 0px 1px ${COLORS.neutral_400};
  box-sizing: border-box;
  border-radius: 4px;
  cursor: pointer;

  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;

  input {
    font-size: inherit;
    line-height: inherit;
    color: inherit;
  }

  ${TooltipTrigger('hover')}

  &::placeholder {
    ${ComponentXXSStyling(ComponentTextStyle.Italic, COLORS.neutral_600)}
  }

  ${BREAKPOINTS.MEDIUM} {
    ${ComponentMStyling(ComponentTextStyle.Regular, 'inherit')}
    padding: 16px;
    height: 56px;

    &::placeholder {
      ${ComponentXSStyling(ComponentTextStyle.Italic, COLORS.neutral_600)}
    }
  }

  &.small {
    ${ComponentSStyling(ComponentTextStyle.Regular, 'inherit')}
    padding: 12px 16px;
    height: 48px;

    &::placeholder {
      ${ComponentXXSStyling(ComponentTextStyle.Italic, COLORS.neutral_600)}
    }
  }

  &.medium {
    ${ComponentMStyling(ComponentTextStyle.Regular, 'inherit')}
    height: 56px;
    padding: 16px;

    &::placeholder {
      ${ComponentXSStyling(ComponentTextStyle.Italic, COLORS.neutral_600)}
    }
  }

  .dropdown-arrow {
    pointer-events: none;
    width: 24px;
    height: 24px;
    color: ${(props) => props.isPlaceholder ? COLORS.neutral_600 : undefined};
  }

  &:focus {
    outline: 3px solid #ddd;
  }

  &:focus-within,
  &.focus-visible-within {
    box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_800};
  }

  &:hover:not(.action-within) {
    box-shadow: inset 0px 0px 0px 1px ${COLORS.primary_200};

    input {
      ::placeholder {
        color: ${COLORS.primary_600};
      }
    }

    .dropdown-arrow {
      color: ${COLORS.primary_600};
    }

    cursor: pointer;
  }

  &:active:not(.action-within) {
    box-shadow: inset 0px 0px 0px 1px ${COLORS.primary_300};

    input {
      ::placeholder {
        color: ${COLORS.primary_800};
      }
    }

    .dropdown-arrow {
      color: ${COLORS.primary_800};
    }
  }

  &.button {
    ${(props) => ComponentSStyling(ComponentTextStyle.Bold, props.disabled ? COLORS.neutral_300 : COLORS.neutral_600)}

    box-sizing: border-box;
    height: 32px;
    padding: 0 8px;
    width: initial;
    min-width: initial;
    border: none !important;
    box-shadow: none;
    border-radius: 4px;

    &.expanded {
      color: ${COLORS.neutral_800};
      background: ${COLORS.neutral_20};
    }
  }


  ${(props) => (props.disabled ? disabledState : '')}
  ${(props) => (props.readOnly ? lockedState : '')}
  ${(props) => (props.showValidationMessage ? activeValidationMessage : '')}
  ${(props) => (props.isPlaceholder ? placeholderStyling : '')}

  ${CommonInteractionStyling};

  &.focus-visible,
  &.focus-visible-within {
    z-index: ${Z_INDEXES.dropdown + 1} !important;
    ${focusStyles}
  }
`;

export const DropdownContentButtonStyling = css`
  min-width: 344px;
`;



export const DropdownButtonTextContent = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  box-sizing: border-box;
  min-width: 64px;
  min-height: 32px;
  padding: 4px 8px;
  border-radius: 4px;
`;

export const DropdownButtonTextIcon = styled.div`
  width: 24px;
  height: 24px;
`;

export const DropdownButtonTextArrow = styled.div`
  width: 24px;
  height: 24px;

  svg {
    pointer-events: none;
  }
`;

export const DropdownButtonTextStyles = styled.div`
  flex: 1
`;

export const DropdownButtonTextWrapper = styled.div`
  box-sizing: border-box;
  min-height: 48px;
  display: flex;
  align-items: center;
  width: fit-content;
  cursor: pointer;

  ${ComponentSStyling(ComponentTextStyle.Bold, COLORS.neutral_600)}

  .expanded {
    color: ${COLORS.neutral_800};

    ${DropdownButtonTextContent} {
      background: ${COLORS.neutral_20};
    }
  }

  &:focus {
    ${focusStyles}
  }

  &:hover:not(.disabled) {
    ${DropdownButtonTextContent} {
      background-color: ${COLORS.primary_20};
      color: ${COLORS.primary_700};
    }
  }

  &:active:not(.disabled) {
    ${DropdownButtonTextContent} {
      background-color: ${COLORS.primary_100};
      color: ${COLORS.primary_800};
    }
  }

  &.disabled {
    cursor: not-allowed;
    color: ${COLORS.neutral_300};
  }

}`;
